import { LegacyVoice } from '../types/legacy';

export type AgeGroup = 'YOUNG' | 'ADULT' | 'SENIOR';
export type VoiceGender = 'M' | 'F';
export type SsmlGender = 'MALE' | 'FEMALE' | 'NEUTRAL';

export interface VoiceProfile {
  name: string;
  languageCode: string;
  ssmlGender: SsmlGender;
  pitch: number;
  speakingRate: number;
  description: string;
  ageGroup: AgeGroup;
}

const DEFAULT_AGE_GROUP: AgeGroup = 'ADULT';

const voiceTemplates: Record<VoiceGender, Record<AgeGroup, Omit<VoiceProfile, 'ageGroup'>>> = {
  M: {
    YOUNG: {
      name: 'it-IT-Standard-D',
      languageCode: 'it-IT',
      ssmlGender: 'MALE',
      pitch: 2,
      speakingRate: 1.05,
      description: 'Voce maschile giovane e brillante.',
    },
    ADULT: {
      name: 'it-IT-Standard-C',
      languageCode: 'it-IT',
      ssmlGender: 'MALE',
      pitch: 0,
      speakingRate: 1,
      description: 'Voce maschile adulta, nitida e ferma.',
    },
    SENIOR: {
      name: 'it-IT-Standard-C',
      languageCode: 'it-IT',
      ssmlGender: 'MALE',
      pitch: -6,
      speakingRate: 0.78,
      description: 'Voce maschile anziana, profonda e lenta.',
    },
  },
  F: {
    YOUNG: {
      name: 'it-IT-Standard-B',
      languageCode: 'it-IT',
      ssmlGender: 'FEMALE',
      pitch: 3,
      speakingRate: 1.08,
      description: 'Voce femminile giovane e vivace.',
    },
    ADULT: {
      name: 'it-IT-Standard-A',
      languageCode: 'it-IT',
      ssmlGender: 'FEMALE',
      pitch: 1.5,
      speakingRate: 1.05,
      description: 'Voce femminile adulta, luminosa e sicura.',
    },
    SENIOR: {
      name: 'it-IT-Standard-A',
      languageCode: 'it-IT',
      ssmlGender: 'FEMALE',
      pitch: -3.5,
      speakingRate: 0.82,
      description: 'Voce femminile anziana, morbida e pacata.',
    },
  },
};

const ITALIAN_VOICE_NAME_REGEX = /^it-IT-(?:Standard|Wavenet|Neural2)-[A-Z]$/i;

const clamp = (value: number, min: number, max: number, fallback: number): number => {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(min, Math.min(max, value));
};

export const buildVoiceProfile = (
  gender: VoiceGender = 'M',
  ageGroup: AgeGroup = DEFAULT_AGE_GROUP,
): VoiceProfile => {
  const template = voiceTemplates[gender]?.[ageGroup] || voiceTemplates[gender][DEFAULT_AGE_GROUP];
  return { ...template, ageGroup };
};

export const normalizeLegacyGender = (value: string | null | undefined): VoiceGender => {
  const normalized = String(value || '').toLowerCase();
  return normalized.includes('fem') || normalized === 'f' ? 'F' : 'M';
};

export const resolveLegacyVoiceCode = (voice: LegacyVoice | null | undefined): string => {
  if (!voice) return '';
  const joined = `${voice.name || ''} ${voice.type || ''}`;
  const match = joined.match(/[a-z]{2}-[A-Z]{2}-(?:Standard|Wavenet|Neural2)-[A-Z]/);
  return match?.[0] || '';
};

export const validateAndFixVoiceProfile = (
  voiceProfile: Partial<VoiceProfile>,
  expectedGender: VoiceGender,
): VoiceProfile => {
  const fallback = buildVoiceProfile(expectedGender, DEFAULT_AGE_GROUP);
  const expectedSsml: SsmlGender = expectedGender === 'F' ? 'FEMALE' : 'MALE';
  const ssmlGenderRaw = String(voiceProfile.ssmlGender || '').toUpperCase();
  const ssmlGender: SsmlGender =
    ssmlGenderRaw === 'MALE' || ssmlGenderRaw === 'FEMALE' || ssmlGenderRaw === 'NEUTRAL'
      ? (ssmlGenderRaw as SsmlGender)
      : fallback.ssmlGender;
  const fixedSsmlGender =
    ssmlGender !== expectedSsml && ssmlGender !== 'NEUTRAL' ? expectedSsml : ssmlGender;

  const rawName = String(voiceProfile.name || '').trim();
  const isItalian = rawName.startsWith('it-IT-');
  const fixedName = isItalian && ITALIAN_VOICE_NAME_REGEX.test(rawName) ? rawName : fallback.name;

  return {
    name: fixedName,
    languageCode: 'it-IT',
    ssmlGender: fixedSsmlGender,
    pitch: clamp(Number(voiceProfile.pitch), -10, 10, fallback.pitch),
    speakingRate: clamp(Number(voiceProfile.speakingRate), 0.7, 1.2, fallback.speakingRate),
    description: String(voiceProfile.description || fallback.description).trim(),
    ageGroup: fallback.ageGroup,
  };
};

export const fromLegacyVoice = (voice: LegacyVoice | null | undefined): VoiceProfile | null => {
  if (!voice) return null;
  const name = String(voice.type || '').trim() || resolveLegacyVoiceCode(voice);
  if (!name) return null;
  const expectedGender = normalizeLegacyGender(voice.gender);
  return validateAndFixVoiceProfile(
    {
      name,
      ssmlGender: String(voice.gender || '').toUpperCase() as SsmlGender,
      pitch: Number(voice.pitch ?? 0),
      speakingRate: Number(voice.speed ?? 1),
      description: String(voice.name || ''),
    },
    expectedGender,
  );
};

const normalize = (value: string | null | undefined): string =>
  String(value || '').trim().toLowerCase();

const inferCategoryFromGoogleId = (googleVoiceId: string): 'neural' | 'wavenet' | 'standard' | 'other' => {
  const id = normalize(googleVoiceId);
  if (id.includes('neural')) return 'neural';
  if (id.includes('wavenet')) return 'wavenet';
  if (id.includes('standard')) return 'standard';
  return 'other';
};

const scoreLegacyVoiceForGoogle = (
  voice: LegacyVoice,
  params: { googleVoiceId: string; googleGender?: string },
): number => {
  const code = normalize(resolveLegacyVoiceCode(voice));
  const name = normalize(voice.name);
  const type = normalize(voice.type);
  const gender = normalize(voice.gender);
  const combined = `${name} ${type} ${code}`;
  const googleId = normalize(params.googleVoiceId);
  const category = inferCategoryFromGoogleId(params.googleVoiceId);
  const preferredFemale = normalize(params.googleGender).includes('fem');
  const preferredMale = normalize(params.googleGender).includes('male');

  let score = 0;
  if (code && googleId.includes(code)) score += 1000;
  if (preferredFemale && gender.includes('fem')) score += 150;
  if (preferredMale && (gender.includes('masc') || gender.includes('male'))) score += 150;

  if (category === 'neural' && combined.includes('neural')) score += 120;
  if (category === 'wavenet' && combined.includes('wavenet')) score += 120;
  if (category === 'standard' && combined.includes('standard')) score += 90;

  // Fallback morbidi
  if (combined.includes('adult')) score += 40;
  if (combined.includes('anzian')) score += 20;
  if (combined.includes('strega')) score += 10;
  if (combined.includes('bambin')) score -= 80;

  return score;
};

/**
 * Mappa una voce Google (es. it-IT-Neural2-A) a una voce legacy disponibile.
 * Se non esiste match diretto per codice, usa fallback per genere/categoria.
 */
export const resolveBestLegacyVoiceForGoogle = (
  googleVoiceId: string,
  voices: LegacyVoice[],
  googleGender?: string,
): LegacyVoice | null => {
  if (!Array.isArray(voices) || voices.length === 0) return null;
  const ranked = voices
    .map((voice) => ({
      voice,
      score: scoreLegacyVoiceForGoogle(voice, { googleVoiceId, googleGender }),
    }))
    .sort((a, b) => b.score - a.score);
  return ranked[0]?.voice || null;
};

